import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { productsSelector, usersSelector } from 'src/state/selectors'
import InfoPanel from 'src/global/InfoPanel'
import { Screen } from 'src/screens'
import { getProductLimit, isPaying, isAfterCutoff } from '@meniudigital/shared'

// TODO: Update cutoff date before release
export default function ProductLimitWarning() {
  const { list: products } = useSelector(productsSelector)
  const { user } = useSelector(usersSelector)
  const navigate = useNavigate()

  // Paying users have no limits
  if (isPaying(user)) {
    return null
  }

  const hasEverPayed = user?.subscriptionExpiresAt

  const maxProductsForFree = getProductLimit(user)
  const productCount = products.length
  const isOverLimit = productCount > maxProductsForFree
  const isExactlyAtLimit = productCount === maxProductsForFree
  const isNearLimit = productCount >= maxProductsForFree - maxProductsForFree * 0.25 // 25% away from limit
  const isBeforeCutoff = !isAfterCutoff(user)

  // Worst case: Over the limit
  if (isOverLimit) {
    return (
      <InfoPanel
        type="warning"
        text={`Meniul tău depășește numărul de produse care se pot adăuga în varianta de probă (${productCount}/${maxProductsForFree}). Alege un abonament pentru a continua.`}
        actionButton={{
          label: 'Alege abonament',
          onClick: () => navigate(Screen.RenewSubscription),
        }}
      />
    )
  }

  if (isExactlyAtLimit) {
    return (
      <InfoPanel
        type="warning"
        text={`Meniul tău a atins numărul de produse care se pot adăuga în varianta de probă (${productCount}/${maxProductsForFree}). Alege un abonament pentru a continua.`}
        actionButton={{
          label: 'Alege abonament',
          onClick: () => navigate(Screen.RenewSubscription),
        }}
      />
    )
  }

  // Close to limit: Show warning
  if (isNearLimit) {
    return (
      <InfoPanel
        type="warning"
        text={`Te apropii de numărul maxim de produse din varianta de probă (${productCount}/${maxProductsForFree}).`}
        actionButton={{
          label: 'Alege abonament',
          onClick: () => navigate(Screen.RenewSubscription),
        }}
      />
    )
  }

  // No products yet: Don't show anything
  if (productCount < 1) {
    return null
  }

  // Special case: Before cutoff users who are under the limit get the upgrade message
  if (isBeforeCutoff) {
    if (hasEverPayed) {
      return (
        <InfoPanel
          text={`Nu ai un abonament activ. Poți folosi toate funcționalitățile platformei gratuit, singura limitare fiind numărul de produse, de ${maxProductsForFree}. După aceea, poți achiziționa un abonament din meniul principal ↗️ pentru a continua.`}
          storageId="free-usage-info"
          storageType="sessionStorage"
          type="error"
          actionButton={{
            label: 'Ok',
          }}
        />
      )
    } else {
      return (
        <InfoPanel
          textComponent={
            <span>
              <strong style={{ fontWeight: '600' }}>Contul tău tocmai a fost upgradat!</strong> Acum ai acces la majoritatea
              funcționalităților premium, conform noii structuri de prețuri.{' '}
              <a style={{ textDecoration: 'underline' }} href="http://meniudigital.ro/blog/meniu-digital-pret-simplificat">
                Află mai multe
              </a>
              <br />
              <br />
              Dacă dorești să susții proiectul MeniuDigital și să contribui la menținerea platformei, poți oricând să{' '}
              <a
                style={{ textDecoration: 'underline' }}
                onClick={() => navigate(hasEverPayed ? Screen.RenewSubscription : Screen.ChooseSubscription)}
              >
                achiziționezi un abonament
              </a>
              .
            </span>
          }
          storageId="upgraded-account-info"
          storageType="localStorage"
          actionButton={{
            label: 'Ok, super!',
          }}
        />
      )
    }
  }

  // Normal case: Show info about trial limits
  return (
    <InfoPanel
      text={`Poți folosi toate funcționalitățile platformei gratuit până când te decizi, singura limitare fiind numărul de produse, de ${maxProductsForFree}. După aceea, poți achiziționa un abonament din meniul principal ↗️ pentru a continua.`}
      storageId="free-usage-info"
      storageType="sessionStorage"
      actionButton={{
        label: 'Ok, super!',
      }}
    />
  )
}

export async function requestLimitIncrease() {
  await window.showConfirm({
    title: 'Solicită creșterea limitei de produse',
    textComponent: (
      <span>
        În cadrul efortului nostru de a susține restaurantele de dimensiuni mici și medii cu buget redus, oferim posibilitatea de
        a solicita creșterea limitei de produse, în mod gratuit.
        <br />
        <br />
        Pentru a solicita creșterea limitei, trimite-ne un email la adresa de contact de pe site, în care să explici dimensiunea
        businessului tău și motivul pentru care ai nevoie de o creștere a limitei de produse.
        <br />
        <br />
        Vom analiza fiecare cerere individual și ne vom strădui să găsim o soluție care să te ajute.
      </span>
    ),
    confirmButtonText: 'Ok',
  })
}
